// ---------------------------------------------------------------------------
// CardGrid — responsive card grid (props in → UI out).
//
// Lays out one card per item in an auto-filling responsive grid. The cards
// themselves are the caller's `.card`-classed nodes (the design-system card
// surface); this wrapper owns only the grid container. There is no dedicated
// grid-container class in components.css, so the layout (a single
// `repeat(auto-fill, minmax(<minColWidth>, 1fr))` track) is expressed as an
// inline style — layout geometry only, never color — mirroring the prototype's
// CardsView. The styling contract permits non-color inline styles + token
// values; no off-system class or color literal is introduced.
//
// A pure rendering of the items the caller already holds — no fetching,
// sorting, or filtering. Surface-agnostic and dumb: no privileged logic, no
// Norwegian strings — safe for the public, sessionless register bundle.
// Owns scrolling inside height-constrained shells, with columns that also fit
// containers narrower than the preferred card width.
// ---------------------------------------------------------------------------

import type { CSSProperties } from 'react'

export interface CardGridProps<T> {
  /** Items to render, in the order the caller supplies. */
  items: T[]
  /** Extracts a stable unique string key from an item (React key). */
  itemKey: (item: T) => string
  /** Renders the card for an item (caller returns a `.card`-classed node). */
  renderItem: (item: T) => React.ReactNode
  /** Minimum column width before the grid wraps to fewer columns. Default 320px. */
  minColWidth?: number
  /** Translated name for the keyboard-scrollable card collection. */
  ariaLabel?: string
}

/**
 * Auto-filling responsive card grid.
 *
 * Usage:
 *   <CardGrid
 *     items={cases}
 *     itemKey={(c) => c.uuid}
 *     renderItem={(c) => <CaseCard row={c} t={t} onOpen={open} />}
 *   />
 */
export function CardGrid<T>({ items, itemKey, renderItem, minColWidth = 320, ariaLabel }: CardGridProps<T>) {
  const style: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${minColWidth}px), 1fr))`,
    gap: 12,
    alignContent: 'start',
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: '16px 24px',
  }

  return (
    <div className="card-grid" style={style} tabIndex={0} role={ariaLabel ? 'region' : undefined} aria-label={ariaLabel}>
      {items.map((item) => (
        <div key={itemKey(item)}>{renderItem(item)}</div>
      ))}
    </div>
  )
}
